/**
 * Firebase web SDK configuration for FCM push notifications.
 *
 * These values are PUBLIC (embedded in the client bundle — NOT secrets). The
 * Firebase Admin SDK service account (private key) is server-side only and
 * lives in `functions/functions/API2/firebase-service-account.json`.
 *
 * Go to Firebase Console → Project Settings → General → "Your apps" → Web app
 * to obtain these. If a web app doesn't exist yet, create one (nickname e.g.
 * "nibni-web"). The `projectId` is `nibniapp` (confirmed from the service
 * account). The `messagingSenderId` is the "Cloud Messaging" sender ID visible
 * in Project Settings → Cloud Messaging (legacy) or in the web app config.
 *
 * Runtime override: set `window.__NIBNI_FIREBASE_CONFIG__` (e.g. in index.html)
 * to override the defaults below without rebuilding. Useful for prod.
 */

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Firebase web app config for project `nibniapp` (filled 2026-07-07 from
// Firebase Console → Project Settings → General → Your apps → Web app).
// These are PUBLIC values embedded in the client bundle (not secrets). To
// rotate, update here AND in `client/public/firebase-messaging-sw.js` (keep
// both in sync).
const DEFAULT_CONFIG: FirebaseWebConfig = {
  apiKey: 'AIzaSyCathsg8T8tvkWO3DYND51oYSPexu9wwpw',
  authDomain: 'nibniapp.firebaseapp.com',
  projectId: 'nibniapp',
  storageBucket: 'nibniapp.firebasestorage.app',
  messagingSenderId: '626953549507',
  appId: '1:626953549507:web:b854e1f40dae5c2ff79476',
  measurementId: 'G-DEVEM957F3'
};

function readFromWindow(): Partial<FirebaseWebConfig> | null {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  const cfg = w?.__NIBNI_FIREBASE_CONFIG__;
  if (cfg && typeof cfg === 'object') return cfg as Partial<FirebaseWebConfig>;
  return null;
}

function readFromMeta(): Partial<FirebaseWebConfig> | null {
  if (typeof document === 'undefined' || !document.querySelector) return null;
  const meta = document.querySelector('meta[name="nibni-firebase-config"]') as HTMLMetaElement | null;
  if (!meta || !meta.content) return null;
  try {
    return JSON.parse(meta.content) as Partial<FirebaseWebConfig>;
  } catch {
    return null;
  }
}

function merge(base: FirebaseWebConfig, overrides: Partial<FirebaseWebConfig> | null): FirebaseWebConfig {
  if (!overrides) return base;
  const out: FirebaseWebConfig = { ...base };
  (Object.keys(base) as (keyof FirebaseWebConfig)[]).forEach((key) => {
    const v = (overrides as any)[key];
    if (typeof v === 'string' && v.trim()) {
      (out as any)[key] = v.trim();
    }
  });
  return out;
}

export function resolveFirebaseConfig(): FirebaseWebConfig {
  const overrides = readFromWindow() || readFromMeta();
  return merge(DEFAULT_CONFIG, overrides);
}

export function isFirebaseConfigured(): boolean {
  const cfg = resolveFirebaseConfig();
  return (
    !!cfg.apiKey &&
    !cfg.apiKey.includes('PLACEHOLDER') &&
    !!cfg.messagingSenderId &&
    !cfg.messagingSenderId.includes('PLACEHOLDER') &&
    !!cfg.appId &&
    !cfg.appId.includes('PLACEHOLDER')
  );
}
